// Shared pipeline-stage pill. One visual language for "where is this
// person in the BBC lifecycle?" across Recruitment, Staff Directory,
// Schedule, Profile, and Dashboard.
//
// Always pass the `stage` object returned by the server (computed by
// helpers/pipelineStage.js). The frontend doesn't recompute — it just
// renders.

const COLOR_THEME = {
  gray:   'bg-gray-100 text-gray-700 border-gray-200',
  blue:   'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  amber:  'bg-amber-100 text-amber-700 border-amber-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  cyan:   'bg-cyan-100 text-cyan-800 border-cyan-200',
  green:  'bg-green-100 text-green-700 border-green-200',
  red:    'bg-red-100 text-red-700 border-red-200',
};

export default function PipelineStage({ stage, size = 'md', className = '' }) {
  if (!stage) return null;
  const palette = COLOR_THEME[stage.color] || COLOR_THEME.gray;
  const sizeCls = size === 'sm'
    ? 'text-[10px] px-1.5 py-0.5'
    : size === 'lg'
      ? 'text-sm px-3 py-1'
      : 'text-xs px-2 py-0.5';
  return (
    <span
      title={`Stage ${stage.ordinal || '?'} — ${stage.label}`}
      className={`inline-flex items-center font-medium rounded-full border ${palette} ${sizeCls} ${className}`}>
      {stage.label}
    </span>
  );
}

// ─── Horizontal stage tracker ──────────────────────────────────
// Used on the Candidate Detail header. Draws the happy-path stages as
// a chevron-style breadcrumb with the current stage highlighted.

const HAPPY_PATH = [
  { id: 'applied',          label: 'Applied'      },
  { id: 'screening',        label: 'Screening'    },
  { id: 'interviewing',     label: 'Interviewing' },
  { id: 'offer_extended',   label: 'Offer'        },
  { id: 'hired_clearance',  label: 'Clearance'    },
  { id: 'pending_start',    label: 'Pre-Start'    },
  { id: 'onboarding',       label: 'Onboarding'   }, // first_week renders here too
  { id: 'active_probation', label: 'Probation'    },
  { id: 'active',           label: 'Active'       },
];

export function PipelineTracker({ stage }) {
  if (!stage) return null;
  // Treat first_week as same slot as onboarding for the bar.
  const currentId = stage.id === 'first_week' ? 'onboarding' : stage.id;
  // Off-path stages (no_hire, talent_pool, paused, terminated) don't fit
  // the happy-path bar — render just a single chip.
  const offPath = !HAPPY_PATH.some(s => s.id === currentId);
  if (offPath) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3">
        <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide">Pipeline</span>
        <PipelineStage stage={stage} size="md" />
      </div>
    );
  }
  const currentIdx = HAPPY_PATH.findIndex(s => s.id === currentId);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wide">Pipeline</span>
        <PipelineStage stage={stage} size="sm" />
      </div>
      <ol className="flex items-center gap-1 overflow-x-auto">
        {HAPPY_PATH.map((s, idx) => {
          const passed = idx < currentIdx;
          const current = idx === currentIdx;
          const dotCls = passed
            ? 'bg-green-500 text-white'
            : current
              ? 'bg-orange-500 text-white ring-4 ring-orange-100'
              : 'bg-gray-200 text-gray-400';
          const labelCls = passed
            ? 'text-green-700 font-medium'
            : current
              ? 'text-orange-700 font-semibold'
              : 'text-gray-400';
          return (
            <li key={s.id} className="flex items-center gap-1.5 min-w-fit">
              <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${dotCls}`}>
                {passed ? '✓' : idx + 1}
              </span>
              <span className={`text-[11px] ${labelCls}`}>{s.label}</span>
              {idx < HAPPY_PATH.length - 1 && <span className="text-gray-300 px-1">›</span>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
